import React, {useEffect, useRef} from 'react';
import {
    Button,
    Checkbox,
    Container,
    FormControlLabel,
    LinearProgress,
    TextField, Select, InputLabel, FormControl
} from '@material-ui/core';
import {Line} from 'react-chartjs-2';
import styles from '../styles/pages/Sentiment.module.css';
import {
    getArticlesSentimentData,
    getSiteFilterData,
    SentimentResponseTO,
    Series,
    SiteFilterData
} from '../rest/client';
import {ChartData, ChartDataSets, ChartOptions, ChartPoint} from 'chart.js';
import * as _ from 'lodash';
import MenuItem from "@material-ui/core/MenuItem";
import ArticleSentimentStatsTable, {ArticleSentimentStatsRow} from "../controls/ArticleSentimentStasTable";
import useMediaQuery from "../hooks/useMediaQuery";
import breakpoints from "../styles/Breakpoints.module.css";
import { zoomOptions } from '../helpers/chartZoom';
import 'chartjs-plugin-zoom';

const ArticlesSentiment: React.FC = () => {
    const [data, setData] = React.useState<Map<string, SentimentResponseTO>>(new Map<string, SentimentResponseTO>());
    const [keyword, setKeyword] = React.useState<string>();
    const [startMonth, setStartMonth] = React.useState<number>(1);
    const [startYear, setStartYear] = React.useState<number>(1998);
    const [endYear, setEndYear] = React.useState<number>(2020);
    const [error, setError] = React.useState<string>();
    const [inProgress, setInProgress] = React.useState(false);
    const [allSites, setAllSites] = React.useState<SiteFilterData[]>(Array<SiteFilterData>());
    const [selectedSites, setSelectedSites] = React.useState<Set<string>>(new Set<string>());
    const isMobileScreen = useMediaQuery(breakpoints.sm);

    const months = [
        {id: 1, name: 'January'},
        {id: 2, name: 'February'},
        {id: 3, name: 'March'},
        {id: 4, name: 'April'},
        {id: 5, name: 'May'},
        {id: 6, name: 'June'},
        {id: 7, name: 'July'},
        {id: 8, name: 'August'},
        {id: 9, name: 'September'},
        {id: 10, name: 'October'},
        {id: 11, name: 'November'},
        {id: 12, name: 'December'}
    ]

    useEffect(() => {
        getSiteFilterData().then(sites => {
            setAllSites(sites)
            setSelectedSites(new Set(sites.map(s => s.name)))
        })
    }, [])

    const siteUrlByName = (siteName: string): string => {
        return allSites.find(s => s.name === siteName)!.url
    }

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setKeyword(event.target.value);
    };
    const handleStartYearChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        let year = Number(event.target.value);
        setStartYear(year)
    };
    const handleStartMonthChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        let month = Number(event.target.value);
        setStartMonth(month)
    };
    const handleEndYearChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        let year = Number(event.target.value);
        setEndYear(year)
    };
    const handleNewSearch = () => {
        if (keyword && keyword.length > 0 && startYear && endYear) {
            setError(undefined);
            setInProgress(true);
            getArticlesSentimentData(keyword, startMonth, startYear, endYear, Array.from(selectedSites.values())).then(d => {
                setData(d);
                setInProgress(false);
            }).catch(err => {
                setError(err.toString());
                setInProgress(false);
            })
        }
    };

    const average = (arr: number[]): number => {
        return arr.length > 0
            ? _.sum(arr) / arr.length
            : 0
    }

    const getArticleSentimentStatsRows = (): ArticleSentimentStatsRow[] => {
        if (data.size === 0) {
            return []
        } else {
            return [...data.entries()].map(
              ([siteName, d]: [string, SentimentResponseTO]) => ({
                  siteUrl: siteUrlByName(siteName),
                  fullUrl: formatSiteUrl(siteUrlByName(siteName)),
                  totalNumberOfPoints: d.all.y.length,
                  totalAverageSentiment: average(d.all.y),
                  keywordNumberOfPoints: d.withKeyword.y.length,
                  keywordAverageSentiment: average(d.withKeyword.y)
              })
            ).sort((a, b) => a.fullUrl.localeCompare(b.fullUrl));
        }
    }

    let monthMenuItems = months.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)

    let yearMenuItems = _.range(1998, 2021).map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)

    let siteCheckboxes = allSites
        .map(s =>
            ({
                name: s.name,
                url: formatSiteUrl(s.url)
            }))
        .sort((a, b) => a.url.localeCompare(b.url))
        .map(s =>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={selectedSites.has(s.name)}
                        onChange={(e, c) => {
                            if (c) {
                                let sites = selectedSites
                                sites.add(s.name)
                                setSelectedSites(new Set(sites))
                            } else {
                                let sites = selectedSites
                                sites.delete(s.name)
                                setSelectedSites(new Set(sites))

                            }
                        }}
                        color="primary"
                    />
                }
                label={s.url}
            />)

    const chartRef = useRef<Line>(null);
    const handleZoomReset = () => {
        (chartRef.current?.chartInstance as any).resetZoom()
    }
    return <Container maxWidth='xl'>
        <div className={styles.chartContainer}>
            <Button
                variant="outlined"
                size="small"
                className={styles.resetZoom}
                color="primary"
                disabled={inProgress}
                onClick={handleZoomReset}>
                Reset Zoom
            </Button>
            <Line data={getChartData(data, allSites)}
                  options={getChartOptions(isMobileScreen)}
                  ref={chartRef}
            />
        </div>
        {inProgress && <LinearProgress color="secondary" className={styles.progress}/>}
        {error && <span className={styles.error}>{error}</span>}
        <div className={styles.checkboxContainer}>
            {siteCheckboxes}
        </div>
        <div className={styles.searchContainer}>
            <form className={styles.searchForm} noValidate autoComplete="off">
                <FormControl className={styles.formControl}>
                    <InputLabel id="start-month-select-label">Start Month</InputLabel>
                    <Select
                        labelId="start-month-select-label"
                        id="select-start-dt"
                        value={startMonth}
                        onChange={handleStartMonthChange}
                    >{monthMenuItems}</Select>
                </FormControl>
                <FormControl className={styles.formControl}>
                    <InputLabel id="start-dt-select-label">Start Year</InputLabel>
                    <Select
                        labelId="start-dt-select-label"
                        id="select-start-dt"
                        value={startYear}
                        onChange={handleStartYearChange}
                    >{yearMenuItems}</Select>
                </FormControl>
                <FormControl className={styles.formControl}>
                    <InputLabel id="end-dt-select-label">End Year</InputLabel>
                    <Select
                        labelId="end-dt-select-label"
                        id="select-end-dt"
                        value={endYear}
                        onChange={handleEndYearChange}
                    >{yearMenuItems}</Select>
                </FormControl>
            </form>
            <div className={styles.searchButtonContainer}>
                <TextField
                    id="outlined-basic"
                    label="Search text"
                    variant="outlined"
                    className={styles.searchControl}
                    value={keyword}
                    onChange={handleSearchTextChange}
                    onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleNewSearch();
                        }
                    }}
                />
                <div className={styles.searchButton}>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={inProgress}
                        onClick={handleNewSearch}>
                        Search
                    </Button>
                </div>
            </div>
        </div>
        <ArticleSentimentStatsTable rows={getArticleSentimentStatsRows()} showTotal={true}/>
    </Container>
}

export default ArticlesSentiment;

const convertData = (s: Series): ChartPoint[] => {
    let zipped = _.zip(s.x, s.y)
    return _.map(zipped, xy => ({x: xy[0], y: xy[1]} as ChartPoint))
}

const rgbToHex = (r: number, g: number, b: number): string => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

const colorsMap: Map<string, string[]> = new Map(Object.entries({
    "TehranTimes": [rgbToHex(114, 147, 203), rgbToHex(57, 106, 177)],
    "Kayhan": [rgbToHex(225, 151, 76), rgbToHex(218, 124, 48)],
    "FinancialTribune": [rgbToHex(132, 186, 91), rgbToHex(62, 150, 81)],
    "IranDaily": [rgbToHex(211, 94, 96), rgbToHex(204, 37, 41)],
    "IranNewsDaily": [rgbToHex(144, 103, 167), rgbToHex(107, 76, 154)]
}))

const getSiteColors = (siteName: string): string[] => {
    return colorsMap.get(siteName) || [rgbToHex(114, 147, 203), rgbToHex(57, 106, 177)]
}

const formatSiteUrl = (url: string): string => {
    let schema = new RegExp("https?://")
    let tailSlash = new RegExp("/$")
    return url.replace(schema, "").replace(tailSlash, "")
}

const getChartData = (data: Map<string, SentimentResponseTO>, sites: SiteFilterData[]): ChartData => {
    let dataset: ChartDataSets[] = new Array<ChartDataSets>()
    data.forEach((d, siteName) => {
        let site = sites.find(s => s.name === siteName)
        if (site) {
            let formattedUrl = formatSiteUrl(site!.url)
            let colors = getSiteColors(siteName)
            dataset.push(
                {
                    data: convertData(d.all),
                    label: formattedUrl,
                    lineTension: 0,
                    borderColor: colors[0],
                    fill: false
                }, {
                    data: convertData(d.withKeyword),
                    label: formattedUrl + " & keyword",
                    lineTension: 0,
                    borderColor: colors[1],
                    fill: false
                }
            )
        }
    })
    dataset.sort((a, b) => a.label!.localeCompare(b.label!))
    return {
        datasets: dataset
    }
}

const getChartOptions = (isMobile?: boolean): ChartOptions => ({
    maintainAspectRatio: false,
    title: {
        display: true,
        text: 'Sentiment analysis'
    },
    scales: {
        xAxes: [{
            type: 'time',
            time: {
                unit: 'month',
                tooltipFormat: 'MMM DD, YYYY'
            }
        }],
        yAxes: [{
            ticks: {
                beginAtZero: true   // minimum value will be 0.
            }
        }]
    },
    legend: {
        position: isMobile ? "top" : "right",
        labels: {
            filter: (legendItem, chartData) => {
                return legendItem && legendItem.text && legendItem.text.length > 1
            }
        }
    },
    plugins: { zoom: zoomOptions }
})
